import { useWindowSize } from "@hooks/window-size";
import { useRouter } from "next/router";
import React, { FC, useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "next-i18next";
import date_es from "date-fns/locale/es";
import date_de from "date-fns/locale/de";
import * as dateFns from "date-fns";
import {
  BookingDatePickerProps,
  BookingFormData,
} from "./interfaces";
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
} from "react-hook-form";
import styled from "styled-components";
import { Loader } from "@atoms/loader/loader";
import { DateRange, Range } from "react-date-range";

function DateRangeComponent({
  field,
  fieldState,
  onGetLockDates,
  numberOfMonth,
  loadingLockedDates,
  lockedDates,
}: {
  fieldState: ControllerFieldState;
  field: ControllerRenderProps<BookingFormData, "period">;
  onGetLockDates: (date: Date) => void;
  numberOfMonth: number;
  loadingLockedDates: boolean;
  lockedDates: Date[];
}) {
  const { locale } = useRouter();
  const { value, onChange } = field;

  const calendarLocale = useMemo(
    () => ({ es: date_es, de: date_de }[locale ?? "en"]),
    [locale]
  );

  const ranges: Range[] = useMemo(
    () => [
      {
        startDate: value?.checkIn ?? new Date(),
        endDate: value?.checkOut ?? new Date(),
        key: "selection",
      } as any,
    ],
    [value]
  );

  const handleOnChangeCalendar = useCallback(
    (item: any) => {
      if (!item.selection.startDate || !item.selection.endDate) return;

      onChange({
        checkIn: item.selection.startDate,
        checkOut: item.selection.endDate,
      });
    },
    [onChange]
  );

  const handleDisabledDay = useCallback(
    (date: any) => dateFns.isBefore(date, new Date()) || dateFns.isToday(date),
    []
  );

  return (
    <CalendarContainer>
      <DateRange
        onChange={handleOnChangeCalendar}
        disabledDay={handleDisabledDay}
        onShownDateChange={onGetLockDates}
        showDateDisplay
        showPreview
        moveRangeOnFirstSelection={false}
        months={numberOfMonth}
        ranges={ranges}
        direction="horizontal"
        showMonthAndYearPickers={false}
        disabledDates={lockedDates}
        locale={calendarLocale}
        dragSelectionEnabled
      />
      {fieldState.invalid && (
        <span className="calendar-error">{fieldState.error?.message}</span>
      )}
      {loadingLockedDates && (
        <div className="loader-container">
          <Loader variant="dots" />
        </div>
      )}
    </CalendarContainer>
  );
}

export const BookingDatePicker: FC<BookingDatePickerProps> = ({
  onGetCalendar,
  control,
}) => {
  const { t } = useTranslation();
  const [lockedDates, setLockedDates] = useState<Date[]>([]);
  const [loadingLockedDates, setLoadingLockedDates] = useState(false);
  const [minStay, setMinStay] = useState<number | null>(null);
  const { width } = useWindowSize();
  const { period } = control._defaultValues;

  const numberOfMonth = useMemo(
    () => (!!width && width < 768 ? 1 : 2),
    [width]
  );

  const handleGetLockDates = useCallback(
    (date: Date) => {
      const now = new Date();
      const to = dateFns.endOfMonth(dateFns.addMonths(date, numberOfMonth - 1));

      if (dateFns.isBefore(to, now)) {
        return;
      }

      // adjust today
      let from = dateFns.startOfMonth(date);
      if (dateFns.isBefore(from, now)) {
        from = dateFns.addDays(now, 1);
      }

      setLoadingLockedDates(true);
      onGetCalendar(from, to)
        .then((result) => {
          setLockedDates(result.locked_dates);
          setMinStay(result.min_stay);
        })
        .finally(() => {
          setLoadingLockedDates(false);
        });
    },
    [onGetCalendar, numberOfMonth]
  );

  useEffect(() => {
    handleGetLockDates(period?.checkIn ?? new Date());
  }, [handleGetLockDates, period?.checkIn]);

  return (
    <Controller
      name="period"
      control={control}
      rules={{
        required: t("forms:validation.required").toString(),
        validate: (data) => {
          if (!!data && !!data.checkIn && !!data.checkOut) {
            if (!!minStay) {
              const minCheckOutDate = dateFns.addDays(
                data.checkIn,
                minStay - 1
              );
              if (dateFns.isAfter(minCheckOutDate, data.checkOut)) {
                return `Minimum ${minStay} nights.`;
              }
            }
            return true;
          } else {
            return t("forms:validation.select-range-date").toString();
          }
        },
      }}
      render={({ field, fieldState }) => (
        <DateRangeComponent
          field={field}
          fieldState={fieldState}
          onGetLockDates={handleGetLockDates}
          loadingLockedDates={loadingLockedDates}
          lockedDates={lockedDates}
          numberOfMonth={numberOfMonth}
        />
      )}
    />
  );
};

const CalendarContainer = styled.div`
  position: relative;
  margin: 1rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .calendar-error {
    color: #c82929;
    margin: 0.5rem 1rem;
    display: block;
    text-align: center;
  }

  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: #eeeeee6a;
    z-index: 1000;
  }
`;
